import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import React, { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react';
import UrlValidationService from '../../services/UrlValidationService';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import { useTranslation } from 'react-i18next';
import { ICompetitor } from '../../shared/interfaces/ICompetitor';
import CompanyNameService from '../../services/CompanyNameService';
import PlatformService from '../../services/PlatformService';
import PlaceIDFinder from '../PlaceIDFinder/PlaceIDFinder';

interface Props {
  platforms: IPlatform[];
  platform: string;
  companyUrl: string;
  competitors: ICompetitor[];
  setCompetitors: Dispatch<SetStateAction<ICompetitor[]>>;
}
const BenchmarkingQuickSetup: FC<Props> = ({
  platforms,
  platform,
  companyUrl,
  competitors,
  setCompetitors,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [refreshIdFinder, doRefreshIdFinder] = useState(0);

  const [competitorMonitoringLabel, setCompetitorMonitoringLabel] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [isInvalidCompetitorUrl, setIsInvalidCompetitorUrl] = useState(false);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);

  const getCompetitorUrlLabel = (company: string) => {
    const url = platforms.find((v) => v.label === company)?.url || '';
    return isInvalidCompetitorUrl
      ? `${t('invalid_competitor_url')}`
      : `${t('company_url_label_competitor')} ` + url;
  };

  const validateCompetitorUrl = async () => {
    if (platform.toLowerCase() === 'googleplace') {
      setIsInvalidCompetitorUrl(false);
      return true;
    }
    const isUrlValid = await UrlValidationService.validateUrl(competitorUrl);
    if (isUrlValid) {
      setIsInvalidCompetitorUrl(false);
      return true;
    } else {
      setIsInvalidCompetitorUrl(true);
      return false;
    }
  };

  const handleCompetitorMonitoringLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCompetitorMonitoringLabel(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
      <Typography
        variant="body2"
        sx={{ mt: theme.spacing(1), fontWeight: 700 }}
      >
        {t('competitors')}:
      </Typography>
      {competitors.length > 0 && (
        <Box sx={{ backgroundColor: theme.palette.background.default, p: theme.spacing(1) }}>
          {competitors.map((c, index) => {
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  mb: index < competitors.length - 1 ? theme.spacing(2) : 0,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="caption">
                    {t('competitors')} {index + 1}:
                  </Typography>
                  <Typography variant="body2">Label: {c.label}</Typography>
                  <Typography
                    variant="body2"
                    sx={{ width: 0, minWidth: '100%' }}
                  >
                    URL: {c.url}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCompetitors((prevState) => {
                        const newState = [...prevState];
                        newState.splice(index, 1);
                        return [...newState];
                      });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {competitors.length < 4 && (
        <>
          <TextField
            label={t('optional_label')}
            placeholder={`${t('example_company_placeholder')} (${platform})`}
            variant="standard"
            value={competitorMonitoringLabel}
            onChange={handleCompetitorMonitoringLabelChange}
            sx={{ mt: theme.spacing(1), minWidth: '400px' }}
          />
          {platform === 'googleplace' ? (
            <Box sx={{ mt: theme.spacing(1), maxWidth: '400px' }}>
              <PlaceIDFinder
                refresh={refreshIdFinder}
                onChange={setCompetitorUrl}
              />
            </Box>
          ) : (
            <TextField
              label={getCompetitorUrlLabel(platform)}
              error={isInvalidCompetitorUrl}
              variant="standard"
              value={competitorUrl}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setCompetitorUrl(event.target.value);
                setIsInvalidCompetitorUrl(false);
              }}
              sx={{ mt: theme.spacing(1), minWidth: '400px' }}
            />
          )}
          {isValidationInProgress && (
            <Box>
              <LinearProgress sx={{ my: theme.spacing(1) }} />
            </Box>
          )}
          <Button
            disabled={!competitorUrl || isValidationInProgress || isInvalidCompetitorUrl}
            variant="contained"
            onClick={async () => {
              setIsValidationInProgress(true);
              const isUrlValid = await validateCompetitorUrl();
              setIsValidationInProgress(false);

              if (!isUrlValid) return false;

              if (
                competitors.find((competitor) => competitor.url === competitorUrl) !== undefined ||
                competitorUrl === companyUrl
              ) {
                setCompetitorMonitoringLabel('');
                setCompetitorUrl('');
                return false;
              }

              const data = await CompanyNameService.getCompanyName(
                PlatformService.getPlatformId(platform, platforms),
                competitorUrl
              );

              setCompetitors((prevState) => {
                return [
                  ...prevState,
                  {
                    label: competitorMonitoringLabel,
                    company: data,
                    url: competitorUrl,
                  },
                ];
              });

              setCompetitorMonitoringLabel('');
              setCompetitorUrl('');
              doRefreshIdFinder((prev) => prev + 1);
            }}
            sx={{ mt: 1 }}
          >
            {t('add')}
          </Button>
        </>
      )}
    </Box>
  );
};

export default BenchmarkingQuickSetup;
